import {
  AgreementStatus,
  CollateralMethod,
  CollateralType,
  Currency,
  DeliveryType,
  PbDecimal,
  PbDecimalSchema,
  RoundingRule,
  SettlementSystem,
  SettlementType,
  Side,
  TermType,
  agreementStatuses,
  collateralMethods,
  collateralTypes,
  create,
  currencies,
  deliveryTypes,
  roundingRules,
  securityCategories,
  securityTypes,
  settlementSystems,
  settlementTypes,
  sides,
  termTypes,
} from '@/connect';
import { SecurityCategory, SecurityType } from '@/connect/gen/consts/instrumentconsts_pb';
import Decimal from 'decimal.js';

export function getAgreementStatusName(
  status: AgreementStatus,
  isIncoming: boolean
): string | undefined {
  if (status === AgreementStatus.DRAFT) {
    return isIncoming ? 'Pending my approval' : 'Pending CP approval';
  }
  return agreementStatuses.get(status)?.name ?? '–';
}

export function getAgreementStatusColor(
  status: AgreementStatus,
  isIncoming: boolean
): string | undefined {
  if (!isIncoming && status === AgreementStatus.DRAFT) {
    return 'gray';
  }
  if (isIncoming && status === AgreementStatus.REJECTED) {
    return 'gray';
  }
  return agreementStatuses.get(status)?.color;
}

export function formatSecurityTypeString(type: SecurityType): string {
  return securityTypes.get(type) ?? '–';
}

export function formatSecurityCategoryString(category: SecurityCategory): string {
  return securityCategories.get(category) ?? '–';
}

export function formatSide(side: Side): string {
  return sides.get(side) ?? '–';
}

export function formatSettlementType(settlementType: SettlementType): string {
  return settlementTypes.get(settlementType) ?? '–';
}

export function formatRoundingRule(roundingRule: RoundingRule): string {
  return roundingRules.get(roundingRule) ?? '–';
}

export function formatProfitCenter(profitCenter: undefined | string): string {
  return profitCenter || '–';
}

export function formatCollateralType(collateralType: CollateralType): string {
  return collateralTypes.get(collateralType) ?? '–';
}

export function formatCurrency(currency: undefined | Currency): string {
  return currency === undefined ? '–' : (currencies.get(currency) ?? '–');
}

export function formatDeliveryType(deliveryType: DeliveryType): string {
  return deliveryTypes.get(deliveryType) ?? '–';
}

export function formatCollateralMethod(collateralMethod: CollateralMethod): string {
  return collateralMethods.get(collateralMethod) ?? '–';
}

export function formatTermType(termType: TermType): string {
  return termTypes.get(termType) ?? '–';
}

export function formatSettlementSystem(settlementSystem?: SettlementSystem): string {
  return (settlementSystem === undefined ? null : settlementSystems.get(settlementSystem)) ?? '–';
}

export function bufferToBigInt(buf: Uint8Array): bigint {
  const bits = BigInt(buf.BYTES_PER_ELEMENT * 8);

  let result = 0n;
  for (const i of buf.values()) {
    const bi = BigInt(i);
    result = (result << bits) + bi;
  }
  return result;
}

export function bigIntToBuffer(value: bigint): Uint8Array<ArrayBuffer> {
  if (value < 0n) {
    throw new RangeError('Negative values are not supported');
  }

  const byteArray: number[] = [];
  while (value > 0n) {
    byteArray.unshift(Number(value & 0xffn));
    value >>= 8n;
  }

  return Uint8Array.from(byteArray);
}

export function pbDecimalToDecimal(value: PbDecimal): Decimal {
  const { sign, exponent, coefficient } = value;
  return new Decimal(`${sign > 0 ? '+' : '-'}${bufferToBigInt(coefficient) || '0'}e${exponent}`);
}

export function decimalToPbDecimal(value: Decimal): PbDecimal {
  return create(PbDecimalSchema, {
    sign: value.s,
    exponent: value.e,
    coefficient: bigIntToBuffer(BigInt(value.d.join(''))),
  });
}
